import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Save, Key, Users, Check, X, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '@/components/EmptyState';
import useLaboratories from '@/hooks/useLaboratories';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';
import { useStats } from '@/contexts/StatsContext';

// `technical_review` přidáno pro technickou kontrolu metrologem.
const ACTIONS = ['view', 'calibrate', 'technical_review', 'approve'];
const ACTION_LABELS = {
  view: 'Prohlížení',
  calibrate: 'Pracování',
  technical_review: 'Tech. kontrola (metrolog)',
  approve: 'Schvalování (vedoucí)',
};

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [password, setPassword] = useState('');
  const [search, setSearch] = useState('');
  // Čekající self-registrace zákazníků z portálu.
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const { refetch: refetchStats } = useStats();

  // Seznam laboratoří (per-modul oprávnění) z centrálního hooku — sjednocuje
  // labely napříč stránkami. Načítá se async, takže je krátce `[]` (prázdná tabulka).
  const labs = useLaboratories();
  const MODULES = labs.map((l) => ({ id: l.code, label: l.name }));

  const loadUsers = () => api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  const loadPending = () => api.get('/users/pending-registrations').then(r => setPending(r.data)).catch(() => {});
  useEffect(() => { loadUsers(); loadPending(); }, []);

  const approveRegistration = async (u) => {
    setBusyId(u.id);
    try {
      await api.post(`/users/${u.id}/approve-registration`);
      toast.success(`Registrace schválena — ${u.firstName} ${u.lastName}`);
      await Promise.all([loadPending(), loadUsers()]);
      refetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při schvalování');
    } finally {
      setBusyId(null);
    }
  };

  const rejectRegistration = async (u) => {
    if (!window.confirm(`Opravdu zamítnout registraci ${u.firstName} ${u.lastName} (${u.companyName || u.email})? Účet i založená firma se smažou.`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/users/${u.id}/reject-registration`);
      toast.success('Registrace zamítnuta');
      await loadPending();
      refetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při zamítnutí');
    } finally {
      setBusyId(null);
    }
  };

  const selectUser = (u) => {
    setSelected(u.id);
    setForm({ ...u });
    setPassword('');
  };

  const createUser = async () => {
    try {
      const res = await api.post('/users', { username: 'novy_uzivatel', firstName: 'Nový', lastName: 'Uživatel', password: 'heslo123' });
      loadUsers();
      selectUser(res.data);
      toast.success('Uživatel vytvořen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při vytváření');
    }
  };

  const saveUser = async () => {
    if (!selected) return;
    try {
      const { id, passwordHash, ...data } = form;
      await api.put(`/users/${selected}`, data);
      loadUsers();
      toast.success('Uloženo');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při ukládání');
    }
  };

  const savePassword = async () => {
    if (!selected || !password) return;
    try {
      await api.put(`/users/${selected}/password`, { password });
      setPassword('');
      toast.success('Heslo změněno');
    } catch {
      toast.error('Chyba při změně hesla');
    }
  };

  const togglePermission = (moduleId, action) => {
    const perms = [...(form.permissions || [])];
    const idx = perms.findIndex(p => p.module === moduleId);
    if (idx === -1) {
      perms.push({ module: moduleId, actions: [action] });
    } else {
      const acts = perms[idx].actions.includes(action)
        ? perms[idx].actions.filter(a => a !== action)
        : [...perms[idx].actions, action];
      perms[idx] = { ...perms[idx], actions: acts };
    }
    setForm({ ...form, permissions: perms });
  };

  const hasPermission = (moduleId, action) => {
    return (form.permissions || []).some(p => p.module === moduleId && p.actions?.includes(action));
  };

  const filtered = users.filter(u =>
    `${u.lastName} ${u.firstName} ${u.username}`.toLowerCase().includes(search.toLowerCase())
  );

  const sel = useBulkSelect(filtered.map(u => u.id));

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    // Backend chrání vlastní účet i posledního admina (HTTP 400) → počítá se do `failed`.
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/users/${id}`));
    if (selected && ids.includes(selected)) { setSelected(null); }
    sel.clear();
    loadUsers();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} nešlo smazat (vlastní účet / poslední admin)`);
    else toast.success(`Smazáno ${ok}`);
  };

  return (
    <div data-testid="user-management-page" className="flex flex-col gap-4">
      {/* Čekající registrace zákazníků (self-registrace z portálu) */}
      {pending.length > 0 && (
        <div className="ct-panel flex-shrink-0">
          <div className="ct-panel-header flex items-center gap-2">
            <UserPlus className="w-4 h-4" style={{ color: 'var(--ct-accent)' }} />
            <span>Čekající registrace zákazníků</span>
            <span
              className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ background: 'var(--ct-warning)', color: '#fff' }}
            >
              {pending.length}
            </span>
          </div>
          <div className="p-3 space-y-2">
            {pending.map(u => (
              <div
                key={u.id}
                data-testid={`pending-reg-${u.id}`}
                className="flex items-center gap-3 p-2 rounded"
                style={{ border: '1px solid var(--ct-border)' }}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-sm">
                    {u.firstName} {u.lastName}
                    {u.companyName ? <span style={{ color: 'var(--ct-text-secondary)' }}> — {u.companyName}</span> : null}
                  </div>
                  <div className="text-xs truncate" style={{ color: 'var(--ct-text-secondary)' }}>
                    {u.email}{u.phoneMobile ? ` · ${u.phoneMobile}` : ''}
                  </div>
                </div>
                <Button
                  data-testid={`approve-reg-${u.id}`}
                  onClick={() => approveRegistration(u)}
                  disabled={busyId === u.id}
                  size="sm"
                  className="ct-btn-primary gap-1"
                >
                  <Check className="w-3 h-3" /> Schválit
                </Button>
                <Button
                  data-testid={`reject-reg-${u.id}`}
                  onClick={() => rejectRegistration(u)}
                  disabled={busyId === u.id}
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  style={{ color: 'var(--ct-error)', borderColor: 'var(--ct-border)' }}
                >
                  <X className="w-3 h-3" /> Zamítnout
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

    <div className="flex gap-4 h-[calc(100vh-7rem)]">
      {/* Left: User list */}
      <div className="ct-panel w-80 flex-shrink-0 flex flex-col">
        <div className="ct-panel-header flex items-center justify-between">
          <span>Uživatelé</span>
          <button data-testid="add-user-btn" onClick={createUser} className="p-1 rounded" style={{ color: 'var(--ct-accent)' }}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="p-2 space-y-2">
          <Input
            data-testid="user-search"
            placeholder="Hledat uživatele..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
          <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="uživatelů" />
        </div>
        <ScrollArea className="flex-1">
          {filtered.map(u => (
            <div
              key={u.id}
              className={`ct-table-row flex items-center gap-2 px-3 py-2 text-sm ${selected === u.id ? 'active' : ''}`}
            >
              <Checkbox
                data-testid={`user-select-${u.id}`}
                checked={sel.isSelected(u.id)}
                onCheckedChange={() => sel.toggle(u.id)}
              />
              <button
                data-testid={`user-item-${u.id}`}
                onClick={() => selectUser(u)}
                className="flex-1 text-left min-w-0"
              >
                <div className="font-medium truncate">{u.lastName} {u.firstName}</div>
                <div className="text-xs truncate" style={{ color: 'var(--ct-text-secondary)' }}>{u.username}</div>
              </button>
            </div>
          ))}
        </ScrollArea>
      </div>

      {/* Right: Detail */}
      {selected ? (
        <div className="flex-1 flex flex-col gap-4 overflow-auto">
          <div className="ct-panel">
            <div className="ct-panel-header">Detail vybraného uživatele</div>
            <div className="p-4 grid grid-cols-2 gap-4">
              <div className="ct-form-field">
                <label className="ct-form-label">Křestní jméno</label>
                <Input data-testid="user-firstName" value={form.firstName || ''} onChange={e => setForm({ ...form, firstName: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Příjmení</label>
                <Input data-testid="user-lastName" value={form.lastName || ''} onChange={e => setForm({ ...form, lastName: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Uživatelské jméno / login</label>
                <Input data-testid="user-username" value={form.username || ''} onChange={e => setForm({ ...form, username: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">E-mail</label>
                <Input data-testid="user-email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="flex items-center gap-3 col-span-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    data-testid="user-isActive"
                    checked={form.isActive ?? true}
                    onCheckedChange={v => setForm({ ...form, isActive: v })}
                  />
                  <span className="text-sm">Aktivní uživatel</span>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    data-testid="user-isAdmin"
                    checked={form.isAdmin ?? false}
                    onCheckedChange={v => setForm({ ...form, isAdmin: v })}
                  />
                  <span className="text-sm">Administrátor aplikace</span>
                </div>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <div className="flex-1 flex gap-2">
                  <Input data-testid="user-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Nové heslo" className="h-9 text-sm" />
                  <Button data-testid="save-password-btn" onClick={savePassword} size="sm" className="ct-btn-secondary gap-1">
                    <Key className="w-3 h-3" /> Uložit heslo
                  </Button>
                </div>
              </div>
            </div>
            <div className="px-4 pb-4">
              <Button data-testid="save-user-btn" onClick={saveUser} className="ct-btn-primary gap-1">
                <Save className="w-4 h-4" /> Uložit změny
              </Button>
            </div>
          </div>

          {/* Permissions */}
          <div className="ct-panel">
            <div className="ct-panel-header">Uživatelská práva</div>
            <div className="p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--ct-border)' }}>
                    <th className="text-left py-2 px-2" style={{ color: 'var(--ct-text-secondary)' }}>Modul</th>
                    {ACTIONS.map(a => (
                      <th key={a} className="text-center py-2 px-2" style={{ color: 'var(--ct-text-secondary)' }}>{ACTION_LABELS[a]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map(m => (
                    <tr key={m.id} className="ct-table-row" style={{ borderBottom: '1px solid var(--ct-border)' }}>
                      <td className="py-2 px-2">{m.label}</td>
                      {ACTIONS.map(a => (
                        <td key={a} className="text-center py-2 px-2">
                          <Checkbox
                            checked={hasPermission(m.id, a)}
                            onCheckedChange={() => togglePermission(m.id, a)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={Users}
            title="Vyberte uživatele"
            description="Klikněte na položku v seznamu vlevo pro úpravu oprávnění."
          />
        </div>
      )}
    </div>
    </div>
  );
}
